import { Activity, CircleHelp, Inbox, Merge, ShieldCheck, Wrench, type LucideIcon } from 'lucide-react';

import type {
  AdminAttentionItem,
  AdminAuditEvent,
  AdminHealth,
  AdminHealthCheck,
  AttentionSeverity,
  EntryKind,
  MetricsPeriodKey,
} from '@/admin/adminApi';
import type { BadgeTone } from '@/ui/kit';
import { NAV_ENTRIES, targetPath } from '@/ui/navigation';

/**
 * Regles de presentation du tableau de bord — module **pur**, sans React.
 *
 * Tout ce que l'ecran decide de dire y est : periode, ecart d'une tuile,
 * vocabulaire de la sante, des elements « a traiter » et du journal. Le
 * rendu ne fait que placer ces phrases ; c'est ce qui permet de tester les
 * regles (« pourcentage seulement si `previous` >= 20 », « jamais un UUID »)
 * sans monter un ecran.
 *
 * Aucune valeur n'est calculee ici a partir d'une autre que le serveur rend,
 * sauf l'ecart d'une tuile (`trendOf`), qui est ce que l'ecran promet.
 */

// ---------------------------------------------------------------------------
// Periode
// ---------------------------------------------------------------------------

export const DEFAULT_PERIOD: MetricsPeriodKey = '7d';

export const PERIODS: readonly { readonly key: MetricsPeriodKey; readonly label: string; readonly short: string }[] = [
  { key: '24h', label: '24 heures', short: '24 h' },
  { key: '7d', label: '7 jours', short: '7 j' },
  { key: '30d', label: '30 jours', short: '30 j' },
];

/** Une valeur inconnue (ou absente) retombe sur le defaut : jamais d'erreur d'URL. */
export const parsePeriod = (raw: string | null): MetricsPeriodKey =>
  PERIODS.find((period) => period.key === raw)?.key ?? DEFAULT_PERIOD;

export const periodShort = (key: MetricsPeriodKey): string => PERIODS.find((period) => period.key === key)?.short ?? key;

// ---------------------------------------------------------------------------
// Ecart d'une tuile
// ---------------------------------------------------------------------------

/** En dessous, un pourcentage exagere : 2 comptes de plus sur 4 font +50 %. */
export const MIN_PREVIOUS_FOR_PERCENT = 20;

const signed = (value: number, maximumFractionDigits = 0): string =>
  `${value > 0 ? '+' : value < 0 ? '−' : ''}${Math.abs(value).toLocaleString('fr-FR', { maximumFractionDigits })}`;

export interface Trend {
  readonly label: string;
  readonly tone: BadgeTone;
}

/**
 * Ecart avec la periode precedente.
 *
 * - `previous` absent (`null`) : **aucun ecart**, pas « +100 % » ni « stable » ;
 * - l'ecart absolu est toujours ecrit, le pourcentage seulement si `previous`
 *   atteint `MIN_PREVIOUS_FOR_PERCENT` ;
 * - `higherIsWorse` inverse le ton : plus d'operations rejetees est une mauvaise nouvelle.
 *   Le signe est ecrit dans le libelle, le ton ne porte jamais seul le sens.
 */
export const trendOf = ({
  current,
  previous,
  higherIsWorse = false,
}: {
  readonly current: number;
  readonly previous: number | null;
  readonly higherIsWorse?: boolean;
}): Trend | null => {
  if (previous === null) {
    return null;
  }

  const difference = current - previous;

  if (difference === 0) {
    return { label: 'Stable', tone: 'neutral' };
  }

  const percent = previous >= MIN_PREVIOUS_FOR_PERCENT ? ` · ${signed((difference / previous) * 100, 1)} %` : '';
  const good = higherIsWorse ? difference < 0 : difference > 0;

  return { label: `${signed(difference)}${percent}`, tone: good ? 'success' : 'danger' };
};

// ---------------------------------------------------------------------------
// Sante
// ---------------------------------------------------------------------------

type HealthStatus = AdminHealth['status'];

export const HEALTH_VERDICTS: Readonly<Record<HealthStatus, string>> = {
  ok: 'Tous les services répondent',
  degraded: 'Service dégradé',
  failed: 'Service en échec',
};

const STATUS_WORDS: Readonly<Record<HealthStatus, string>> = { ok: 'OK', degraded: 'Dégradé', failed: 'En échec' };

const CHECK_LABELS: Readonly<Record<string, string>> = {
  database: 'Base de données',
  queue: 'File d’attente',
  failed_transport: 'Traitements en erreur',
  migrations: 'Migrations',
};

/** Libelle d'une verification ; une verification inconnue garde son nom technique. */
export const checkLabel = (name: string): string => CHECK_LABELS[name] ?? name;

/** Espace insecable avant l'unite : « 180 / ms » ne se coupe jamais sur deux lignes. */
const ms = (value: number): string => `${Math.round(value).toLocaleString('fr-FR')}\u00a0ms`;

/**
 * Valeur d'une pastille de verification. `detail` est une phrase technique en
 * anglais (l'API le dit) : elle n'est jamais affichee. Seule la latence l'est,
 * pour la base. La file n'a pas de nombre : « OK » (le compte arrive avec BO-23).
 */
export const checkValue = (check: AdminHealthCheck): string => {
  if (check.status !== 'ok') {
    return check.latencyMs === null ? STATUS_WORDS[check.status] : `${STATUS_WORDS[check.status]} · ${ms(check.latencyMs)}`;
  }

  if (check.name === 'database' && check.latencyMs !== null) {
    return ms(check.latencyMs);
  }

  return check.name === 'migrations' ? 'À jour' : STATUS_WORDS.ok;
};

/** « 42 ms · p95 180 ms », ou « — » sans requete sur la fenetre. */
export const apiValue = (p50Ms: number | null, p95Ms: number | null): string =>
  p50Ms === null ? '—' : `${ms(p50Ms)}${p95Ms === null ? '' : ` · p95 ${ms(p95Ms)}`}`;

/**
 * Segments de la ligne unique de la variante etroite : API, file, migrations, puis
 * tout ce qui n'est pas au vert. Au vert, la verification s'ecrit en un mot
 * (« file OK ») ; sinon, son verdict en toutes lettres (« file : dégradé »), pour
 * qu'une ligne courte ne cache jamais un probleme.
 */
export const compactHealthSegments = (health: AdminHealth, p50Ms: number | null): readonly string[] => {
  const byName = (name: string): AdminHealthCheck | undefined => health.checks.find((check) => check.name === name);
  const segment = (check: AdminHealthCheck | undefined, label: string): string | null => {
    if (check === undefined) {
      return null;
    }

    if (check.status !== 'ok') {
      return `${label} : ${STATUS_WORDS[check.status].toLocaleLowerCase('fr-FR')}`;
    }

    return `${label} ${check.name === 'migrations' ? 'à jour' : 'OK'}`;
  };

  const shown = new Set(['queue', 'migrations']);
  const extras = health.checks.filter((check) => !shown.has(check.name) && check.status !== 'ok');

  return [
    `API ${p50Ms === null ? '—' : ms(p50Ms)}`,
    segment(byName('queue'), 'file'),
    segment(byName('migrations'), 'migrations'),
    ...extras.map((check) => segment(check, checkLabel(check.name).toLocaleLowerCase('fr-FR'))),
  ].filter((part): part is string => part !== null);
};

export const clock = (date: Date): string =>
  date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

// ---------------------------------------------------------------------------
// « A traiter »
// ---------------------------------------------------------------------------

export const SEVERITY_LABELS: Readonly<Record<AttentionSeverity, string>> = {
  critical: 'Critique',
  warning: 'À surveiller',
  info: 'Information',
};

export const SEVERITY_TONES: Readonly<Record<AttentionSeverity, BadgeTone>> = {
  critical: 'danger',
  warning: 'warning',
  info: 'info',
};

const plural = (count: number, one: string, many: string): string => `${count.toLocaleString('fr-FR')} ${count > 1 ? many : one}`;

const instantLabel = (iso: string | undefined): string | null => {
  if (iso === undefined) {
    return null;
  }

  const date = new Date(iso);

  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const SOURCE_LABELS: Readonly<Record<string, string>> = {
  failed_messages: 'Traitements en erreur',
  gdpr_requests: 'Demandes RGPD',
  health: 'Santé du service',
  maintenance: 'Maintenance',
  sync_conflicts: 'Conflits de synchronisation',
};

/** Nom d'une source, pour dire laquelle n'a pas pu repondre. */
export const sourceLabel = (kind: string): string => SOURCE_LABELS[kind] ?? kind;

export interface AttentionLine {
  readonly title: string;
  readonly detail: string | null;
  readonly icon: LucideIcon;
  /** Entree du registre de navigation qui permet d'agir. */
  readonly ownerEntryId: string | null;
}

/** Noms de verifications, dans `latest.label` : traduits quand on les connait. */
const translateChecks = (label: string): string =>
  label
    .split(/[\s,]+/u)
    .filter((word) => word !== '')
    .map(checkLabel)
    .join(', ');

export const describeAttention = (item: AdminAttentionItem): AttentionLine => {
  const at = instantLabel(item.latest?.at);
  const since = instantLabel(item.since ?? undefined);

  switch (item.kind) {
    case 'failed_messages':
      return {
        title: plural(item.count, 'traitement en erreur', 'traitements en erreur'),
        detail:
          item.latest?.label === undefined
            ? null
            : `Dernier : ${item.latest.label}${at === null ? '' : ` · ${at}`}`,
        icon: Inbox,
        ownerEntryId: 'jobs',
      };
    case 'health':
      return {
        title: plural(item.count, 'vérification hors du vert', 'vérifications hors du vert'),
        detail: item.latest?.label === undefined ? null : translateChecks(item.latest.label),
        icon: Activity,
        ownerEntryId: 'health',
      };
    case 'gdpr_requests':
      return {
        title: plural(item.count, 'demande RGPD ouverte', 'demandes RGPD ouvertes'),
        // La gravite dit deja le retard (`critical`) ; ici, seulement depuis quand on attend.
        detail: since === null ? null : `La plus ancienne reçue le ${since}`,
        icon: ShieldCheck,
        ownerEntryId: 'gdpr',
      };
    case 'sync_conflicts':
      return {
        title: plural(item.count, 'conflit de synchronisation ouvert', 'conflits de synchronisation ouverts'),
        // « Ouvert » comprend les conflits renvoyes a l'utilisatrice ; ici, depuis quand le plus ancien attend.
        detail: since === null ? null : `Le plus ancien détecté le ${since}`,
        icon: Merge,
        ownerEntryId: 'conflicts',
      };
    case 'maintenance':
      return {
        title: 'Mode maintenance activé',
        detail: since === null ? null : `Depuis le ${since}`,
        icon: Wrench,
        ownerEntryId: 'global-settings',
      };
    default:
      // Une source que cet ecran ne connait pas reste visible : la masquer
      // serait dire « rien a traiter » pour ce qu'on ne sait pas lire.
      return {
        title: `${item.kind} · ${item.count.toLocaleString('fr-FR')}`,
        detail: since === null ? null : `Depuis le ${since}`,
        icon: CircleHelp,
        ownerEntryId: null,
      };
  }
};

/** Chemin de l'ecran d'une entree du registre, **seulement si son ecran est livre**. */
export const entryPath = (entryId: string | null): string | null => {
  const entry = NAV_ENTRIES.find((candidate) => candidate.id === entryId);

  return entry?.target === undefined ? null : targetPath(entry.target);
};

// ---------------------------------------------------------------------------
// Natures de saisie
// ---------------------------------------------------------------------------

export const ENTRY_KINDS: readonly { readonly kind: EntryKind; readonly label: string }[] = [
  { kind: 'bottle', label: 'Biberons' },
  { kind: 'breastfeeding', label: 'Tétées' },
  { kind: 'pumping', label: 'Tirages' },
  { kind: 'solid_meal', label: 'Repas solides' },
  { kind: 'growth', label: 'Croissance' },
];

/** « 2026-10-04 » -> « 4 oct. », sans passer par un fuseau (la date est celle de Paris, deja posee). */
export const dayLabel = (isoDay: string): string => {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(isoDay);

  return parts === null
    ? isoDay
    : new Date(Date.UTC(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      });
};

// ---------------------------------------------------------------------------
// Journal
// ---------------------------------------------------------------------------

/** Action sensible a recuperer : le journal sans les simples consultations. */
export const EXCLUDED_AUDIT_ACTION = 'admin.access';

// Les libelles d'action vivent dans un seul dictionnaire, `admin/audit/auditPresentation.ts`
// (couvert par `auditActionsCoverage.test.ts`, qui relit les appels du backend) : un second
// dictionnaire ici avait deja derive.

/** L'acteur, par **adresse**. Jamais l'UUID : il ne se lit pas et ne dit rien a un exploitant. */
export const actorLabel = (event: AdminAuditEvent): string =>
  event.actorEmail ?? (event.actorUserId === null ? 'Console' : 'Compte introuvable');

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

/** La cible, quand elle se lit : une adresse, ou un identifiant qui n'est pas un UUID (clef de reglage). */
export const targetLabel = (event: AdminAuditEvent): string | null => {
  if (event.targetEmail !== null) {
    return event.targetEmail;
  }

  return event.targetId === null || UUID.test(event.targetId) || event.targetType === 'route' ? null : event.targetId;
};

/** Heure le jour meme, jour et mois sinon. */
export const eventTime = (iso: string, now: Date): string => {
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toDateString() === now.toDateString()
    ? date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};
